/**
 * BackButton — React web implementation.
 */

import React from 'react';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import { Icon } from '../Icon/Icon.web';
import type { BackButtonProps } from './BackButton';
import {
  BACK_BUTTON_ICON_SIZE,
  BACK_BUTTON_RADIUS,
  BACK_BUTTON_SIZE,
  backButtonBackgroundForState,
  backButtonIconColorForState,
} from './BackButton';

export interface WebBackButtonProps extends BackButtonProps {
  onPress?: () => void;
  'aria-label'?: string;
  'data-testid'?: string;
}

const BACK_BUTTON_FOCUS_STYLE_ID = 'galapagos-back-button-focus-visible';
const BACK_BUTTON_FOCUS_SELECTOR = '[data-galapagos-back-button="true"]';

export function BackButton({
  accessibilityLabel,
  state = 'Default',
  testID,
  onPress,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: WebBackButtonProps) {
  ensureFocusVisibleStyle(BACK_BUTTON_FOCUS_STYLE_ID, [BACK_BUTTON_FOCUS_SELECTOR]);
  const isDisabled = state === 'Disabled';
  const color = backButtonIconColorForState[state];

  const style: React.CSSProperties = {
    width: BACK_BUTTON_SIZE,
    height: BACK_BUTTON_SIZE,
    minWidth: BACK_BUTTON_SIZE,
    minHeight: BACK_BUTTON_SIZE,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: BACK_BUTTON_RADIUS,
    backgroundColor: backButtonBackgroundForState[state],
    color,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
  };

  return (
    <button
      data-analytics-action="ui.back-button.activate"
      type="button"
      aria-label={ariaLabel ?? accessibilityLabel}
      aria-disabled={isDisabled || undefined}
      data-galapagos-back-button="true"
      data-testid={dataTestId ?? testID}
      style={style}
      onClick={(event) => {
        if (isDisabled) {
          event.preventDefault();
          return;
        }
        onPress?.();
      }}
    >
      <Icon name="left-arrow" size={BACK_BUTTON_ICON_SIZE} color={color} aria-hidden />
    </button>
  );
}
